import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {Panel} from 'primeng/panel';
import {TableModule} from 'primeng/table';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {InputText} from 'primeng/inputtext';
import {Divider} from 'primeng/divider';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ConfirmationService} from 'primeng/api';
import {MessageService} from 'primeng/api';
import {DialogModule} from 'primeng/dialog';
import {ToastModule} from 'primeng/toast';
import { CommonEffects } from '../store/common.effects';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { selectAssetCodeData, selectAssetCodeTotal } from '../store/common.selector';
import { AssetCode } from '../../shared/models/common/asset-code.model';
import { selectIsLoadingAction } from '../../shared/store/global';
import { takeUntil } from 'rxjs/operators';
import * as action from '../store/common.actions';
import {toSignal} from '@angular/core/rxjs-interop';
import {BaseListComponent} from '../../shared/components/base-list.component';
import { AssetHistoryDialogComponent } from '../../shared/components/asset-history-dialog/asset-history-dialog.component';
import { ColumnFilters } from '../../shared/models/ui/table-column.model';

@Component({
  selector: 'asset-codes-list',
  templateUrl: './asset-codes-list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Panel,
    TableModule,
    Button,
    IconField,
    InputIcon,
    InputText,
    Divider,
    ConfirmDialogModule,
    DialogModule,
    ToastModule,
    AssetHistoryDialogComponent
  ],
  providers: [ConfirmationService, MessageService, CommonEffects],
  styleUrls: ['./asset-codes-list.component.scss']
})
export class AssetCodesListComponent extends BaseListComponent implements OnInit, OnDestroy {

  private messageService = inject(MessageService);
  private store$ = inject(Store<{ common: any }>)
  private actions$ = inject(Actions);

  assetCodes = toSignal(this.store$.select(selectAssetCodeData), {initialValue: []});
  totalRecords = toSignal(this.store$.select(selectAssetCodeTotal), {initialValue: 0});
  loading = toSignal(this.store$.select(selectIsLoadingAction('[Common] Find Asset Codes')), {initialValue: false});

  
  readonly auditResourceName = 'Asset Code';
  historyVisible = false;
  historyRecordId: number | string | null = null;
  historyRecordLabel = '';

  selectedAssetCodes = signal<AssetCode[]>([]);

  // Add Asset Code Form Variables
  dialogVisible = signal(false);
  assetGroupName = signal('');
  assetGroupCode = signal('');
  assetTypeName = signal('');
  assetTypeCode = signal('');
  savingAssetCode = signal(false);
  isEditMode = signal(false);
  editingAssetCodeId = signal<number | null>(null);

  deleteButtonLabel = computed(() => {
    return this.selectedAssetCodes().length > 1 ? 'Delete Multiple' : 'Delete';
  });

  override ngOnInit(): void {
    super.ngOnInit();

    // Success Alert
    this.actions$.pipe(
      ofType(action.createAssetCodeSuccess, action.updateAssetCodeSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingAssetCode.set(false);
      this.closeDialog();
      this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
    })

    // Delete Success Alert
    this.actions$.pipe(
      ofType(action.deleteAssetCodeSuccess, action.bulkDeleteAssetCodesSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.selectedAssetCodes.set([]);
      this.loadAssetCodes(); 
    })

    // Handle Error
    this.actions$.pipe(
      ofType(action.createAssetCodeError, action.updateAssetCodeError, action.deleteAssetCodeError, action.bulkDeleteAssetCodesError),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingAssetCode.set(false);
    })
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  override loadData(page: number, size: number, filters: ColumnFilters): void {
    const keywords = filters['keywords'] || '';
    this.store$.dispatch(action.findAssetCodes({
      page,
      size,
      keywords
    }));
  }

  loadAssetCodes(): void {
    this.store$.dispatch(action.findAssetCodes({ page: 1, size: this.rows, keywords: ''}))
  }

  addNew(): void {
    this.assetGroupName.set('');
    this.assetGroupCode.set('');
    this.assetTypeName.set('');
    this.assetTypeCode.set('');
    this.isEditMode.set(false);
    this.dialogVisible.set(true);
    this.editingAssetCodeId.set(null);
  }

  closeDialog(): void {
    this.assetGroupName.set('');
    this.assetGroupCode.set('');
    this.assetTypeName.set('');
    this.assetTypeCode.set('');
    this.savingAssetCode.set(false);
    this.isEditMode.set(false);
    this.dialogVisible.set(false);
    this.editingAssetCodeId.set(null);
  }

  saveAssetCode(): void {
    if(!this.assetGroupName().trim() 
      || !this.assetGroupCode().trim() 
      || !this.assetTypeName().trim() 
      || !this.assetTypeCode().trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Asset group name, asset group code, asset type name, and asset type code are required'
      });
      return;
    }

    this.savingAssetCode.set(true);

    const assetCodeData: AssetCode = {
      id: this.editingAssetCodeId() || 0,
      asset_group_name: this.assetGroupName().trim(),
      asset_group_code: this.assetGroupCode().trim(),
      asset_type_name: this.assetTypeName().trim(),
      asset_type_code: this.assetTypeCode().trim()
    };

    if(this.isEditMode() && this.editingAssetCodeId()) {
      this.store$.dispatch(action.updateAssetCode({ id: this.editingAssetCodeId()!, assetCode: assetCodeData }))
    } else {
      this.store$.dispatch(action.createAssetCode({ assetCode: assetCodeData }))
    }
  }

  editAssetCode(): void {
    if (this.selectedAssetCodes().length === 1) {
      const assetCodeToEdit = this.selectedAssetCodes()[0];
      this.assetGroupName.set(assetCodeToEdit.asset_group_name ? assetCodeToEdit.asset_group_name : '');
      this.assetGroupCode.set(assetCodeToEdit.asset_group_code ? assetCodeToEdit.asset_group_code : '');
      this.assetTypeName.set(assetCodeToEdit.asset_type_name ? assetCodeToEdit.asset_type_name : '');
      this.assetTypeCode.set(assetCodeToEdit.asset_type_code ? assetCodeToEdit.asset_type_code : '');
      this.isEditMode.set(true);
      this.editingAssetCodeId.set(assetCodeToEdit.id);
      this.dialogVisible.set(true);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select exactly one asset code to edit'
      });
    }
  }

  deleteAssetCode(): void {
    if (this.selectedAssetCodes().length === 0) {
      return;
    }

    const assetCodesToDelete = [...this.selectedAssetCodes()];
    const count = assetCodesToDelete.length;
    
    let message: string;
    if (count === 1) {
      message = `Are you sure you want to delete "${assetCodesToDelete[0].asset_type_name}" (${assetCodesToDelete[0].asset_type_code})?`;
    } else {
      const maxDisplay = 10;
      const codesToShow = assetCodesToDelete.slice(0, maxDisplay);
      const codesList = codesToShow.map((ac, index) => `${index + 1}. ${ac.asset_type_code} - ${ac.asset_type_name}`).join('<br>');
      
      let fullMessage = `Are you sure you want to delete ${count} asset codes?<br><br><strong>Asset codes to be deleted:</strong><br>${codesList}`;
      
      if (count > maxDisplay) {
        fullMessage += '<br>etc.';
      }
      
      message = fullMessage;
    }

    this.confirmationService.confirm({
      message: message,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (count === 1) {
          this.store$.dispatch(action.deleteAssetCode({ id: assetCodesToDelete[0].id }));
        } else {
          const ids = assetCodesToDelete.map(ac => ac.id);
          this.store$.dispatch(action.bulkDeleteAssetCodes({ ids }));
        }
      }
    });
  }

  refreshAssetCodes(): void {
    this.selectedAssetCodes.set([]);
    this.clearFilter();
    this.messageService.add({
      severity: 'info',
      summary: 'Refreshed',
      detail: 'Asset codes list has been refreshed'
    });
  }

  showHistory(): void {
    const selected = this.selectedAssetCodes();
    if (selected.length !== 1) {
      this.messageService.add({
        severity: 'info',
        summary: 'History',
        detail: 'Select one record to view history'
      });
      return;
    }
    const item = selected[0] as any;
    this.historyRecordId = item?.id ?? null;
    this.historyRecordLabel = item?.asset_code || item?.code || item?.name || String(this.historyRecordId ?? '');
    this.historyVisible = true;
  }
}
